// Guia de uso (abre no primeiro acesso e pelo menu).
// Extraído de App.js sem alteração de comportamento.
import { useState, useEffect, useRef } from "react";
import React from "react";
import { useIsMobile, useTheme } from "../lib/theme";
import { NavIcon } from "./icons";

// ── GUIA DE USO ──────────────────────────────────────────────────────────────
// Abre sozinho no primeiro acesso (profiles.guide_version < GUIDE_VERSION) e
// pode ser reaberto pelo menu ("Guia de uso"; no celular, em "Mais").
// GUIDE_VERSION fica em ./lib/constants.

function buildGuideSteps(isAdmin) {
  const steps = [
    {
      icon: "guia", title: "Bem-vindo ao G17",
      intro: "Seu painel de estudos para concursos policiais: organize a semana, cronometre cada matéria, acompanhe o edital, faça seus resumos e flashcards, registre simulados e compare seu ritmo com a comunidade.",
      items: [
        "Este guia mostra o essencial em cerca de 2 minutos, tela por tela.",
        "Você pode revê-lo quando quiser em \"Guia de uso\", no fim do menu lateral (no celular, em \"Mais\").",
      ],
    },
    {
      icon: "dashboard", screen: "dashboard", title: "Início",
      intro: "A primeira tela resume o seu dia.",
      items: [
        "Progresso de hoje, ritmo da semana e total estudado.",
        "Sugestão do dia: o que estudar agora, com base no seu ciclo.",
        "Sequência: dias seguidos com pelo menos 10 minutos de estudo. Toque num quadradinho para ver o dia. Um dia sem estudar zera a contagem.",
        "Ranking da semana, projeção do próximo simulado e conquistas que você desbloqueia estudando, fazendo resumos e revisando flashcards.",
      ],
    },
    {
      icon: "rotina", screen: "rotina", title: "Rotina",
      intro: "Monte a sua semana de estudos.",
      items: [
        "Crie uma rotina em \"+ Nova rotina\".",
        "Adicione horários em \"+ Novo horário\" ou no \"+\" de cada dia.",
        "A rotina define quanto tempo você tem por semana. O Ciclo usa essa informação para distribuir as matérias.",
      ],
    },
    {
      icon: "ciclo", screen: "ciclo", title: "Ciclo de Estudos",
      intro: "O coração do G17: o ciclo divide o seu tempo entre as matérias do edital.",
      items: [
        "Escolha o concurso (PRF, PF, PCDF, Polícia Penal do PR, Enem e outros) e, quando houver, o cargo. As matérias vêm prontas: o peso segue o número de questões da prova (ou uma estimativa) e as estrelas indicam seu domínio (menos estrelas = mais tempo no ciclo).",
        "O G17 monta blocos intercalados no anel. Toque num bloco para escolher a matéria e em \"Iniciar\" para começar.",
        "\"Divisão do tempo de estudo\" separa revisão e conteúdo novo, usando o tempo que ainda falta no bloco.",
        "No botão ⋮ de cada bloco: inserir tempo já estudado, marcar como concluída ou reiniciar.",
        "Ao trocar de aba, o cronômetro vira um bloco flutuante que você pode arrastar, pausar e continuar. Se der F5 ou fechar a página, a sessão continua de onde parou.",
      ],
    },
    {
      icon: "m_pdf", screen: "ciclo", title: "Sessão de estudo",
      intro: "Cada vez que você inicia um bloco, o G17 abre uma sessão.",
      items: [
        "Escolha o modo: Videoaula, PDF, Letra de lei, Outro ou Livre. Na fase de revisão: Questões, Feynman, Flashcards ou Releitura.",
        "Escolha o assunto do edital (opcional). Matéria, assunto, modo e se é progressão ou revisão aparecem dentro do anel.",
        "Quando o tempo acaba, o G17 apita e continua contando como tempo extra (+00:00:05). Você pode seguir estudando ou finalizar — o tempo extra também conta.",
        "Ao finalizar: marque os assuntos que terminou (o que não terminou fica \"Em andamento\"), ajuste o tempo se precisar e dê sua autoavaliação.",
        "Ajuste para menos tira o tempo também do ranking. Ajuste para mais vai para o calendário e o ciclo, mas não conta no ranking.",
      ],
    },
    {
      icon: "calendario", screen: "calendario", title: "Calendário",
      intro: "Veja quanto estudou de cada matéria, dia a dia.",
      items: [
        "O tempo do Ciclo entra sozinho no dia em que você estudou.",
        "Clique numa célula para lançar ou corrigir tempo à mão (ex.: 1:30).",
        "Botão direito num dia (no celular, segure o dedo) para escrever ou ver anotações. Dias com anotação ganham um triângulo no canto.",
        "Acumulado por matéria: filtre por total, mês, período ou dia.",
      ],
    },
    {
      icon: "edital", screen: "edital", title: "Edital verticalizado",
      intro: "Todos os assuntos do edital, para acompanhar o que já estudou.",
      items: [
        "Disponível para PRF, PF (Agente, Escrivão e Papiloscopista), PCDF (Delegado e Agente) e Polícia Penal do PR, com o conteúdo dos editais oficiais e, quando o edital informa, o número de questões de cada matéria.",
        "O concurso do seu ciclo entra sozinho. No seletor você adiciona outros e alterna entre eles.",
        "Marque os assuntos à mão ou deixe as sessões marcarem por você. Cada matéria mostra a porcentagem concluída e o tempo registrado.",
        "Toque na carinha de um assunto para dar ou trocar sua autoavaliação.",
        "No fim da tela ficam as sessões registradas, por dia, com filtro por matéria.",
      ],
    },
    {
      icon: "resumos", screen: "resumos", title: "Meus Resumos",
      intro: "Seus cadernos de resumo, um para cada matéria.",
      items: [
        "Crie um caderno por matéria e, dentro dele, quantos resumos quiser. Tudo é salvo sozinho enquanto você escreve.",
        "O editor funciona como um Word: títulos, listas com subníveis, tabelas e imagens. Cole, arraste ou anexe a imagem, ajuste o tamanho e posicione ao lado do texto.",
        "Selecione um trecho para deixar em negrito, itálico, sublinhado ou marcar com uma cor.",
        "A busca encontra qualquer palavra em todos os resumos, e \"Tópicos\" leva direto a um título do resumo aberto.",
        "Para organizar: segure a alça ⋮⋮ de um resumo e arraste para mudar a ordem, ou use os botões de ordenar por data.",
        "Já tem resumos no PowerPoint? Em \"Importar do PowerPoint\", escolha os arquivos .pptx (ou um .zip com vários). Cada seção vira um resumo, com as imagens e as cores.",
      ],
    },
    {
      icon: "flashcards", screen: "flashcards", title: "Flashcards",
      intro: "Cartões de memorização com revisão espaçada.",
      items: [
        "Crie baralhos por matéria e separe os cartões por assunto.",
        "Modelos: frente e verso, invertido (pergunta nos dois sentidos), tampar palavras e certo ou errado. No modelo tampar palavras, selecione o trecho e toque em \"Tampar seleção\".",
        "Na revisão, toque no cartão para virar e responda Errei, Difícil, Bom ou Fácil. Cada botão mostra quando o cartão volta; quanto mais fácil, mais tempo até a próxima vez.",
        "Entram até 20 cartões novos por dia. \"Revisar todos\" passa por todos os cartões sem mudar as datas de revisão.",
        "Usa o Anki? Em \"Importar do Anki\", escolha o arquivo .apkg: os baralhos entram separados por matéria e assunto, e você pode manter o progresso das revisões.",
        "No computador, a barra de espaço vira o cartão e as teclas 1 a 4 respondem.",
      ],
    },
    {
      icon: "simulados", screen: "simulados", title: "Simulados",
      intro: "Registre e acompanhe o seu desempenho nas provas.",
      items: [
        "Registrar: lance o resultado por nota direta ou no padrão CEBRASPE (acertos, erros e brancos).",
        "Histórico: todos os simulados registrados.",
        "Projeção: defina nota atual, meta, prazo e frequência para ver se está no ritmo.",
        "Modo Prova: faça o simulado com cronômetro, como no dia da prova.",
      ],
    },
    {
      icon: "taf", screen: "taf", title: "TAF",
      intro: "Treine para o teste de aptidão física e acompanhe sua evolução.",
      items: [
        "Em \"+ Novo plano\", escolha PRF ou PF: os exercícios e índices do edital já vêm prontos, para homem ou mulher. Para outro concurso, cadastre os exercícios e os índices à mão.",
        "Informe a data do TAF. Em cada exercício, \"Registrar\" guarda o seu resultado (repetições, tempo ou distância) e o gráfico mostra a evolução.",
        "A projeção mostra o seu ritmo por semana e como você deve chegar no dia da prova, parecido com os simulados.",
        "Na PRF, \"Ver tabela de pontos\" lista todas as faixas (por exemplo, 35 repetições = 2,0 pontos) e o G17 calcula a sua média. É preciso 2,0 em cada teste e média 3,0. Na PF, vale o índice mínimo de cada teste.",
        "\"Fazer agora\" abre um cronômetro (com o tempo da prova, como os 12 minutos da corrida) para você treinar e registrar ao terminar.",
      ],
    },
    {
      icon: "comunidade", screen: "comunidade", title: "Comunidade e ranking",
      intro: "Estude acompanhado.",
      items: [
        "Publique textos e fotos, curta e comente. A aba \"Sugestões\" é para ideias de melhoria do G17.",
        "Toque no nome de alguém para ver o perfil e as conquistas.",
        "O ranking compara as horas do cronômetro na semana e no total. Tempo inserido à mão não conta.",
        "O sino mostra avisos e novidades.",
      ],
    },
    {
      icon: "perfil", screen: "perfil", title: "Perfil e aparência",
      intro: "Deixe o G17 do seu jeito.",
      items: [
        "Nome público, foto, biografia e concurso almejado (com o brasão ao lado do seu nome).",
        "Suas estatísticas por período e conquistas.",
        "Estilo visual: Clássico, Tático refinado, Foco calmo ou Institucional.",
        "Configurações: lembretes, sons ao completar um bloco, tela de carregamento ao abrir e controles do cronômetro na tela de bloqueio do celular.",
        "Conta: trocar e-mail, trocar senha, verificação em duas etapas, baixar seus dados ou excluir a conta.",
      ],
    },
  ];
  if (isAdmin) {
    steps.push({
      icon: "admin", screen: "admin", title: "Área do administrador",
      intro: "Só você vê esta tela e o botão Admin destacado no menu.",
      items: [
        "Admin: usuários (ordenados por último acesso, criação ou nome), avisos, banimentos e modo manutenção (pede a sua senha de novo).",
        "Atividade: quem está online e em qual tela, onde as pessoas passam o tempo e a linha do tempo de cada usuário (Hoje, 7 ou 30 dias).",
        "No Ciclo, o \"Modo teste\" deixa o tempo fora do ranking e do calendário. \"Reverter teste\" desfaz o ciclo e o que foi marcado no edital.",
      ],
    });
  }
  steps.push({
    icon: "guia", title: "Dicas finais",
    intro: "Pronto para começar!",
    items: [
      "No celular, use a barra de baixo. Rotina, Edital, Meus resumos, Flashcards, TAF, Comunidade, Perfil e este guia ficam em \"Mais\".",
      "Instale o G17 na tela inicial para abrir como um aplicativo. No iPhone: Compartilhar → Adicionar à Tela de Início. No Android: menu ⋮ → Instalar app. No iPhone, os avisos de bloco concluído só funcionam assim.",
      "Comece pela Rotina, crie o seu Ciclo, inicie uma sessão e acompanhe tudo no Edital. Perto da prova física, monte o seu plano no TAF.",
    ],
  });
  return steps;
}

export function GuiaUso({ onClose, onGo, isAdmin }) {
  const t = useTheme();
  const isMobile = useIsMobile();
  const steps = React.useMemo(() => buildGuideSteps(isAdmin), [isAdmin]);
  const [i, setI] = useState(0);
  const step = steps[i];
  const last = i === steps.length - 1;
  const bodyRef = useRef(null);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setI(n => Math.min(steps.length - 1, n + 1));
      else if (e.key === "ArrowLeft") setI(n => Math.max(0, n - 1));
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, steps.length]);

  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, [i]);
  // Leva a pessoa para a tela explicada (a tela real fica visível atrás do guia)
  useEffect(() => { if (step.screen && onGo) onGo(step.screen); }, [i]); // eslint-disable-line react-hooks/exhaustive-deps

  const btnBase = { minHeight: 44, borderRadius: 10, fontSize: "0.9rem", fontWeight: 700, cursor: "pointer", padding: "0 1.1rem", fontFamily: "inherit" };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 9990, background: "rgba(0,0,0,0.22)", display: "flex",
      alignItems: "flex-end", justifyContent: isMobile ? "center" : "flex-end", padding: isMobile ? 0 : "1.5rem" }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="g17-guia-title"
        style={{ width: "100%", maxWidth: isMobile ? "none" : 420, maxHeight: isMobile ? "58dvh" : "min(560px, 86vh)", display: "flex", flexDirection: "column",
          background: t.bgCard, color: t.text, border: `1px solid ${t.border}`, borderRadius: isMobile ? "18px 18px 0 0" : 16,
          boxShadow: t.mode === "dark" ? "0 16px 48px rgba(0,0,0,0.45)" : "0 16px 48px rgba(20,24,40,0.16)", fontFamily: t.fontBody, overflow: "hidden" }}>

        {/* Topo: progresso + fechar */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: isMobile ? "0.9rem 1rem 0" : "1.1rem 1.4rem 0" }}>
          <div style={{ display: "flex", gap: 5, flex: 1 }} aria-hidden="true">
            {steps.map((_, k) => (
              <button key={k} onClick={() => setI(k)} tabIndex={-1}
                style={{ flex: 1, height: 4, borderRadius: 2, border: "none", padding: 0, cursor: "pointer", background: k <= i ? t.ac : t.border, transition: "background 0.2s" }} />
            ))}
          </div>
          <span style={{ fontSize: "0.75rem", color: t.textDim, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{i + 1} de {steps.length}</span>
          <button onClick={onClose} aria-label="Fechar guia"
            style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${t.border}`, background: "transparent", color: t.textDim, cursor: "pointer", fontSize: "1.1rem", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>×</button>
        </div>

        {/* Conteúdo */}
        <div ref={bodyRef} style={{ overflowY: "auto", padding: isMobile ? "1.2rem 1.1rem 0.5rem" : "1.5rem 1.6rem 0.5rem", flex: 1 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: t.acDim, border: `1px solid ${t.acBorder}`, color: t.ac, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1rem" }}>
            <NavIcon id={step.icon} size={24} />
          </div>
          <h2 id="g17-guia-title" style={{ margin: "0 0 0.5rem", fontSize: isMobile ? "1.35rem" : "1.5rem", fontWeight: 800, letterSpacing: "-0.01em", fontFamily: t.fontDisplay, color: t.text }}>{step.title}</h2>
          <p style={{ margin: "0 0 1.1rem", fontSize: "0.95rem", lineHeight: 1.55, color: t.textMid }}>{step.intro}</p>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.7rem" }}>
            {step.items.map((txt, k) => (
              <li key={k} style={{ display: "flex", gap: "0.7rem", fontSize: "0.9rem", lineHeight: 1.55, color: t.textMid }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.ac, marginTop: "0.55em", flexShrink: 0 }} />
                <span>{txt}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Rodapé: navegação */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: isMobile ? "0.9rem 1rem calc(0.9rem + env(safe-area-inset-bottom))" : "1.1rem 1.4rem 1.3rem", borderTop: `1px solid ${t.borderIn}`, marginTop: "0.8rem" }}>
          {!last && (
            <button onClick={onClose} style={{ ...btnBase, background: "transparent", border: "none", color: t.textDim, padding: "0 0.4rem", fontWeight: 600 }}>Pular guia</button>
          )}
          <div style={{ flex: 1 }} />
          {i > 0 && (
            <button onClick={() => setI(i - 1)} style={{ ...btnBase, background: "transparent", border: `1px solid ${t.border}`, color: t.text }}>Voltar</button>
          )}
          <button onClick={() => last ? onClose() : setI(i + 1)}
            style={{ ...btnBase, background: t.ac, border: "none", color: t.onAc || (t.mode === "dark" ? "#000" : "#fff"), minWidth: 110 }}>
            {last ? "Começar" : "Próximo"}
          </button>
        </div>
      </div>
    </div>
  );
}
